import { Link } from 'react-router-dom';
import { LuChevronDown } from '@platform/icon';
import {
  getFaqCategoryLabel,
  getFaqSubCategoryLabel,
} from '../constants/faq.ts';
import type { FaqPost } from '../types/board.type.ts';
import { PostViewer } from './PostViewer.tsx';
import { CATEGORY_CHIP, CATEGORY_CHIP_NEUTRAL } from './chipStyles.ts';

interface FaqAccordionItemProps {
  faq: FaqPost;
  isOpen: boolean;
  onToggle: () => void;
  editPath?: string | null;
  /** 삭제 권한이 없으면 넘기지 않는다 — 버튼이 숨는다. */
  onDelete?: (() => void) | null;
  deleting?: boolean;
}

export function FaqAccordionItem({
  faq,
  isOpen,
  onToggle,
  editPath,
  onDelete,
  deleting = false,
}: FaqAccordionItemProps) {
  const categoryLabel =
    getFaqSubCategoryLabel(faq.category, faq.subCategory) ??
    getFaqCategoryLabel(faq.category);

  return (
    <li className="border-b border-black/[0.06] dark:border-white/10">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        className="flex w-full items-center gap-4 py-5 text-left"
      >
        <span className="text-[15px] font-bold text-[#FA0F00]">Q</span>
        {categoryLabel && (
          <span className={`${CATEGORY_CHIP} ${CATEGORY_CHIP_NEUTRAL} -mr-2`}>
            {categoryLabel}
          </span>
        )}
        <span className="flex-1 text-[16px] font-semibold text-neutral-950 dark:text-neutral-100">
          {faq.title}
        </span>
        <LuChevronDown
          aria-hidden
          size={16}
          className={`shrink-0 text-neutral-950/40 transition-transform duration-200 dark:text-white/40 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
      <div
        className={`grid overflow-hidden transition-[grid-template-rows] duration-300 ease-out ${
          isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        }`}
      >
        <div className="min-h-0">
          <div className="flex gap-4 pb-6 pr-6">
            <span className="text-[15px] font-bold text-neutral-950/30 dark:text-white/30">
              A
            </span>
            <div className="flex-1">
              {faq.body ? (
                <PostViewer content={faq.body} />
              ) : (
                <p className="text-neutral-400 dark:text-neutral-500">
                  답변이 준비 중입니다.
                </p>
              )}
              {(editPath || onDelete) && (
                <div className="mt-4 flex justify-end gap-2">
                  {editPath && (
                    <Link
                      to={editPath}
                      className="inline-flex h-8 items-center rounded-full border border-black/10 px-3 text-[12px] font-semibold text-neutral-950/55 transition-colors hover:text-neutral-950 dark:border-white/15 dark:text-white/55 dark:hover:text-white"
                    >
                      수정
                    </Link>
                  )}
                  {onDelete && (
                    <button
                      type="button"
                      onClick={onDelete}
                      disabled={deleting}
                      className="inline-flex h-8 items-center rounded-full border border-red-500/30 px-3 text-[12px] font-semibold text-red-600 transition-colors hover:bg-red-500/10 disabled:opacity-50 dark:border-red-400/30 dark:text-red-400"
                    >
                      {deleting ? '삭제 중…' : '삭제'}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </li>
  );
}
